import { gas_separator_efficiency } from "@/libs/calculations/gasPhasePVT";
import { calculateNaturalGasSeparation } from "@/libs/calculations/intakes";
import { convert } from "@/libs/units";
import { PercentageUnits } from "@/libs/units/percentage";
import { Override_Options_Enum } from "gql/api";
import type { Correlations } from "../forms/formTypes";
import type { FluidPropertiesContext } from "@/libs/calculations/fluidProperties/d";
import type {
  GasSeparationEfficiencies,
  IntakeInfo
} from "./fluidPropertiesPipelineTypes";

export type NaturalGasSeparationParams = {
  basicData: {
    topCasingInteriorDiameterIn: number;
    bottomCasingInteriorDiameterIn: number;
    topCasingLengthFt: number;
  };
  correlations: Correlations;
  orderedIntakesInfo: IntakeInfo[];
  primaryIntakeOuterDiameterInches: number;
  primaryIntakePortSquareInches: number;
  bottomOfIntakesMDFt: number;
  fluidPropertiesAtIntakeContext: FluidPropertiesContext;
  desiredFlowRates: {
    gasFlowRateSCFD: number;
  };
  naturalGasSeparationOverrideType: Override_Options_Enum | null | undefined;
  naturalOverrideGasSeparationPercent: number;
};

const calculateNaturalGasSeparationEfficiency = (
  params: NaturalGasSeparationParams
): number => {
  if (params.orderedIntakesInfo.length === 0) {
    return 0;
  }

  return calculateNaturalGasSeparation(
    {
      topCasingInnerDiameterInches:
        params.basicData.topCasingInteriorDiameterIn,
      bottomCasingInnerDiameterInches:
        params.basicData.bottomCasingInteriorDiameterIn,
      primaryIntakeOuterDiameterInches: params.primaryIntakeOuterDiameterInches,
      primaryIntakePortSquareInches: params.primaryIntakePortSquareInches,
      liquidFlowRateBBLD:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .liquidFlowRateBBLD,
      gasFlowRateSCFD: params.desiredFlowRates.gasFlowRateSCFD,
      liquidDensityLBMFT3:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .liquidDensityLBMFT3,
      gasDensityLBMFT3:
        params.fluidPropertiesAtIntakeContext.fluidProperties.gasDensityLBMFT3,
      topCasingLengthFt: params.basicData.topCasingLengthFt,
      bottomOfIntakesMDFt: params.bottomOfIntakesMDFt,
      liquidSurfaceTensionDYCM:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .liquidSurfaceTensionDYCM,
      liquidSuperficialVelocityFTSEC:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .liquidSuperficialVelocityFTSEC,
      gasSuperficialVelocityFTSEC:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .gasSuperficialVelocityFTSEC,
      bubbleRiseVerticalVelocityFTSEC:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .bubbleRiseVerticalVelocityFTSEC,
      bubbleRiseRadialVelocityFTSEC:
        params.fluidPropertiesAtIntakeContext.fluidProperties
          .bubbleRiseRadialVelocityFTSEC
    },
    params.correlations?.natural_gas_separation
  );
};

export type IntakeGasSeparationParams = {
  orderedIntakesInfo: IntakeInfo[];
  fluidPropertiesAtIntakeContext: FluidPropertiesContext;
};

const calculateIntakeGasSeparationEfficiency = (
  params: IntakeGasSeparationParams
): number => {
  if (params.orderedIntakesInfo.length === 0) {
    return 0;
  }

  const intakeEfficiencies = params.orderedIntakesInfo.map(r_intake => {
    return gas_separator_efficiency(
      params.fluidPropertiesAtIntakeContext.fluidProperties.liquidFlowRateBBLD,
      params.fluidPropertiesAtIntakeContext.fluidProperties.gasFlowVolumeRatio,
      r_intake.maxRateBpd,
      r_intake.eff1,
      r_intake.eff2
    );
  });

  let flowRemainingRatio = 1;
  let aggregateEff = 0;
  for (const eff of intakeEfficiencies) {
    const ratio = eff * flowRemainingRatio;
    aggregateEff += ratio;
    flowRemainingRatio -= ratio;
  }
  return aggregateEff;
};

export type CalculateGasSeparationEfficienciesParams = {
  basicData: {
    topCasingInteriorDiameterIn: number;
    bottomCasingInteriorDiameterIn: number;
    topCasingLengthFt: number;
  };
  correlations: Correlations;
  orderedIntakesInfo: IntakeInfo[];
  primaryIntakeOuterDiameterInches: number;
  primaryIntakePortSquareInches: number;
  bottomOfIntakesMDFt: number;
  fluidPropertiesAtIntakeWithoutNaturalSeparationContext: FluidPropertiesContext;
  fluidPropertiesAtIntakeContext: FluidPropertiesContext;
  desiredFlowRates: {
    gasFlowRateSCFD: number;
  };
  naturalGasSeparationOverrideType: Override_Options_Enum | null | undefined;
  naturalOverrideGasSeparationPercent: number;
  intakeGasSeparationOverrideType: Override_Options_Enum | null | undefined;
  intakeOverrideGasSeparationPercent: number;
  intakePackerInstalled: boolean;
};

export const calculateGasSeparationEfficiencies = (
  params: CalculateGasSeparationEfficienciesParams
): GasSeparationEfficiencies => {
  // Calculate override ratios
  const overrideNaturalGasSeparationEfficiencyRatio =
    params.naturalGasSeparationOverrideType === Override_Options_Enum.Disable
      ? 0
      : convert(
          params.naturalOverrideGasSeparationPercent,
          PercentageUnits.Percentage,
          PercentageUnits.Ratio
        );

  const calculatedNaturalGasSeparationEfficiencyRatio =
    calculateNaturalGasSeparationEfficiency({
      basicData: params.basicData,
      correlations: params.correlations,
      orderedIntakesInfo: params.orderedIntakesInfo,
      primaryIntakeOuterDiameterInches: params.primaryIntakeOuterDiameterInches,
      primaryIntakePortSquareInches: params.primaryIntakePortSquareInches,
      bottomOfIntakesMDFt: params.bottomOfIntakesMDFt,
      fluidPropertiesAtIntakeContext:
        params.fluidPropertiesAtIntakeWithoutNaturalSeparationContext,
      desiredFlowRates: params.desiredFlowRates,
      naturalGasSeparationOverrideType: params.naturalGasSeparationOverrideType,
      naturalOverrideGasSeparationPercent:
        params.naturalOverrideGasSeparationPercent
    });

  const naturalGasSeparationEfficiencyRatio =
    params.naturalGasSeparationOverrideType === Override_Options_Enum.Calculate
      ? calculatedNaturalGasSeparationEfficiencyRatio
      : overrideNaturalGasSeparationEfficiencyRatio;

  // Intake gas separation
  const overrideIntakeGasSeparationEfficiencyRatio =
    params.intakeGasSeparationOverrideType === Override_Options_Enum.Disable
      ? 0
      : convert(
          params.intakeOverrideGasSeparationPercent,
          PercentageUnits.Percentage,
          PercentageUnits.Ratio
        );

  const calculatedIntakeGasSeparationEfficiencyRatio =
    calculateIntakeGasSeparationEfficiency({
      orderedIntakesInfo: params.orderedIntakesInfo,
      fluidPropertiesAtIntakeContext: params.fluidPropertiesAtIntakeContext
    });

  const intakeGasSeparationEfficiencyRatio = params.intakePackerInstalled
    ? 0
    : params.intakeGasSeparationOverrideType === Override_Options_Enum.Calculate
      ? calculatedIntakeGasSeparationEfficiencyRatio
      : overrideIntakeGasSeparationEfficiencyRatio;

  // Calculate combined gas separation ratio
  let flowRemainingRatio = 1;
  let combinedGasSeparationEfficiencyRatio = 0;
  for (const eff of [
    naturalGasSeparationEfficiencyRatio,
    intakeGasSeparationEfficiencyRatio
  ]) {
    const ratio = eff * flowRemainingRatio;
    combinedGasSeparationEfficiencyRatio += ratio;
    flowRemainingRatio -= ratio;
  }

  return {
    calculatedNaturalGasSeparationEfficiencyRatio,
    overrideNaturalGasSeparationEfficiencyRatio,
    naturalGasSeparationEfficiencyRatio,
    calculatedIntakeGasSeparationEfficiencyRatio,
    overrideIntakeGasSeparationEfficiencyRatio,
    intakeGasSeparationEfficiencyRatio,
    combinedGasSeparationEfficiencyRatio
  };
};
